import { normalizeTags } from "../../articles/tags.js";
const STOP_WORDS = new Set(["a", "an", "and", "as", "at", "by", "for", "from", "in", "is", "of", "on", "or", "the", "to", "with"]);

const NAMED_ENTITIES = { amp: "&", lt: "<", gt: ">", quot: "\"", apos: "'", nbsp: " ", ndash: "–", mdash: "—", hellip: "…", lsquo: "‘", rsquo: "’", ldquo: "“", rdquo: "”" };

export function decodeHtmlEntities(value) {
  return String(value || "").replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, code) => {
    if (code[0] === "#") {
      const point = code[1].toLowerCase() === "x" ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isFinite(point) && point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : entity;
    }
    return NAMED_ENTITIES[code.toLowerCase()] ?? entity;
  });
}

// Readable story text for descriptions: no markup, entities decoded, and no image captions,
// credits, embeds or scripts (those are not the story and must not become the snippet).
export function plainText(value) {
  const storyOnly = String(value || "")
    .replace(/<(script|style|figcaption|iframe)\b[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<div[^>]*data-social-url[^>]*>[\s\S]*?<\/div>/gi, " ");
  return decodeHtmlEntities(storyOnly.replace(/<[^>]*>/g, " ")).replace(/\s+/g, " ").trim();
}

// Takes text that is already plain: running plainText twice would strip decoded "<…>" as tags.
function truncateAtWord(text, limit) {
  if (text.length <= limit) return text;
  const sliced = text.slice(0, limit + 1);
  return `${sliced.slice(0, Math.max(0, sliced.lastIndexOf(" "))).trim()}…`;
}

export function generateSeoTitle(article) {
  return truncateAtWord(plainText(article?.title), 60);
}

export function generateMetaDescription(article) {
  const source = plainText(article?.subtitle) || plainText(article?.content);
  return truncateAtWord(source, 155);
}

export function generateArticleSlug(title) {
  // Possessives and joined abbreviations stay one word: "award's" -> "awards", "R&D" -> "rd".
  const words = plainText(title).toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .replace(/(\w)['\u2019](\w)/g, "$1$2").replace(/(\w)&(\w)/g, "$1$2")
    .replace(/[^a-z0-9\s-]/g, " ").split(/\s+/).filter(Boolean);
  const meaningful = words.filter((word) => !STOP_WORDS.has(word));
  const selected = (meaningful.length >= 3 ? meaningful : words).slice(0, 8);
  return selected.join("-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}

export function generateSocialMetadata({ article, seoTitle, metaDescription }) {
  return {
    title: seoTitle || generateSeoTitle(article),
    description: metaDescription || generateMetaDescription(article),
    image: article?.image || null
  };
}

export function detectPrimaryTopic(article) {
  const tags = normalizeTags(article?.tags);
  return tags[0] || article?.category?.name || article?.category || null;
}

export function generateSeoDefaults(article, existingState = {}) {
  const now = new Date();
  const seoTitle = existingState.seoTitleMode === "MANUAL" && article?.seoTitle ? article.seoTitle : generateSeoTitle(article);
  const metaDescription = existingState.metaDescriptionMode === "MANUAL" && article?.seoDescription ? article.seoDescription : generateMetaDescription(article);
  const slug = existingState.slugMode === "MANUAL" && article?.slug ? article.slug : generateArticleSlug(article?.title);
  const socialMetadata = existingState.socialMetadataMode === "MANUAL" && existingState.socialMetadata
    ? existingState.socialMetadata
    : generateSocialMetadata({ article, seoTitle, metaDescription });
  return { seoTitle, metaDescription, slug, socialMetadata, primaryTopic: detectPrimaryTopic(article), generatedAt: now };
}
